import {
  type Customer,
  CustomerDetail,
  type CustomerDetail as CustomerDetailDto,
  OkResponse,
  OrderStatus,
  QuoteStatus,
  formatBRL,
  formatDate,
  formatIsoDate,
  validTaxId,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { Alert, ErrorBox } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ApiClientError, api, newIdempotencyKey } from '../../lib/api';
import { useChannels } from '../../lib/catalog-api';
import { errorMessage } from '../../lib/messages';
import {
  useCustomer,
  useCustomerHistory,
  useCustomers,
  useInvalidateQuotes,
} from '../../lib/quotes-api';
import { initials, useIsAdmin } from '../../lib/session';
import { useIsPhone } from '../../lib/use-media';
import { StatusBadge } from '../orders/common';
import { STATUS_BADGE, statusLabel } from '../quotes/format';
import { ExportCsvButton } from '../../components/ExportCsv';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

interface Form {
  name: string;
  whatsapp: string;
  email: string;
  nick: string;
  channelId: string;
  notes: string;
  taxId: string;
  address: string;
}

const toForm = (c?: CustomerDetailDto): Form => ({
  name: c?.name ?? '',
  whatsapp: c?.whatsapp ?? '',
  email: c?.email ?? '',
  nick: c?.marketplaceNickname ?? '',
  channelId: c?.preferredChannel?.id ?? '',
  notes: c?.notes ?? '',
  taxId: c?.taxId ?? '',
  address: c?.address ?? '',
});

/** "Novo cliente" / "Editar cliente" (board Clientes), with the optional fiscal section. */
function CustomerDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer?: CustomerDetailDto;
  onClose: () => void;
  onSaved: (c: CustomerDetailDto) => void;
}) {
  const channels = useChannels();
  const invalidate = useInvalidateQuotes();
  const [form, setForm] = useState(() => toForm(customer));
  const [key] = useState(newIdempotencyKey);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        whatsapp: form.whatsapp.trim() || null,
        email: form.email.trim() || null,
        marketplaceNickname: form.nick.trim() || null,
        preferredChannelId: form.channelId || null,
        notes: form.notes.trim() || null,
        taxId: form.taxId.trim() || null,
        address: form.address.trim() || null,
      };
      return customer
        ? api.patch(`/customers/${customer.id}`, CustomerDetail, body)
        : api.post('/customers', CustomerDetail, body, { idempotencyKey: key });
    },
    onSuccess: (c) => {
      invalidate();
      onSaved(c);
    },
    onError: (err) => {
      const details = err instanceof ApiClientError ? JSON.stringify(err.details ?? '') : '';
      if (details.includes('taxId')) setErrors({ taxId: 'CPF ou CNPJ inválido.' });
      else if (details.includes('email')) setErrors({ email: 'E-mail inválido.' });
      else setErrors({ form: errorMessage(err) });
    },
  });
  const submit = () => {
    if (!form.name.trim()) {
      setErrors({ name: 'Informe o nome ou um apelido.' });
      return;
    }
    if (form.taxId.trim() && !validTaxId(form.taxId)) {
      setErrors({ taxId: 'CPF ou CNPJ inválido.' });
      return;
    }
    save.mutate();
  };
  return (
    <Modal
      title={customer ? 'Editar cliente' : 'Novo cliente'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn p" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <Field id="fn" label="Nome" error={errors.name}>
        <input
          id="fn"
          className={errors.name ? 'input err' : 'input'}
          value={form.name}
          onChange={set('name')}
        />
      </Field>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))',
          gap: 12,
        }}
      >
        <Field id="fw" label="WhatsApp">
          <input
            id="fw"
            className="input tab"
            inputMode="tel"
            placeholder="(48) 9 0000-0000"
            value={form.whatsapp}
            onChange={set('whatsapp')}
          />
        </Field>
        <Field id="fe" label="E-mail" error={errors.email}>
          <input
            id="fe"
            className="input"
            placeholder="opcional"
            value={form.email}
            onChange={set('email')}
          />
        </Field>
        <Field id="fa" label="Apelido no marketplace">
          <input
            id="fa"
            className="input"
            placeholder="opcional"
            value={form.nick}
            onChange={set('nick')}
          />
        </Field>
        <Field id="fc" label="Canal preferido">
          <Combobox
            id="fc"
            value={form.channelId}
            options={[
              { value: '', label: 'Nenhum', pinned: 'top' },
              ...(channels.data?.data ?? []).map(channelOption),
            ]}
            onChange={(v) => set('channelId')({ target: { value: v } })}
          />
        </Field>
      </div>
      <Field id="fno" label="Notas">
        <textarea
          id="fno"
          className="input"
          rows={3}
          placeholder="Preferências, como chegou até a loja…"
          value={form.notes}
          onChange={set('notes')}
        />
      </Field>
      <details open={!!(form.taxId || form.address || errors.taxId)}>
        <summary className="sm" style={{ cursor: 'pointer', fontWeight: 500 }}>
          Dados fiscais e endereço (opcional)
        </summary>
        <div className="col" style={{ gap: 12, marginTop: 12 }}>
          <Field id="ft" label="CPF ou CNPJ" error={errors.taxId}>
            <input
              id="ft"
              className={errors.taxId ? 'input tab err' : 'input tab'}
              inputMode="numeric"
              value={form.taxId}
              onChange={set('taxId')}
            />
          </Field>
          <Field id="fad" label="Endereço com CEP" help="Só para nota fiscal ou envio direto.">
            <input id="fad" className="input" value={form.address} onChange={set('address')} />
          </Field>
        </div>
      </details>
      {errors.form ? <p className="help err">{errors.form}</p> : null}
    </Modal>
  );
}

const QUOTE_LIKE = new Set(['draft', 'sent', 'accepted', 'declined', 'expired', 'converted']);

function HistoryBadge({ kind, status }: { kind: 'quote' | 'order'; status: string }) {
  if (kind === 'quote' && QUOTE_LIKE.has(status)) {
    const s = QuoteStatus.parse(status);
    return <span className={STATUS_BADGE[s]}>{statusLabel(s)}</span>;
  }
  const o = OrderStatus.safeParse(status);
  return o.success ? <StatusBadge status={o.data} /> : <span className="badge">{status}</span>;
}

function CustomerDetailView({ id, onBack }: { id: string; onBack?: () => void }) {
  const customer = useCustomer(id);
  const history = useCustomerHistory(id);
  const isAdmin = useIsAdmin();
  const invalidate = useInvalidateQuotes();
  const toast = useToast();
  const [, setParams] = useSearchParams();
  const [dialog, setDialog] = useState<'edit' | 'delete' | 'anonymize' | null>(null);
  const remove = useMutation({
    mutationFn: () => api.delete(`/customers/${id}`, OkResponse),
    onSuccess: () => {
      invalidate();
      toast('Cliente foi para a lixeira');
      setParams({});
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const anonymize = useMutation({
    mutationFn: () => api.post(`/customers/${id}/anonymize`, CustomerDetail),
    onSuccess: () => {
      invalidate();
      setDialog(null);
      toast('Dados pessoais removidos. Valores mantidos nos relatórios.');
    },
    onError: (err) => toast(errorMessage(err)),
  });
  if (customer.error)
    return (
      <ErrorBox
        title="Não foi possível carregar o cliente"
        onRetry={() => void customer.refetch()}
      />
    );
  const c = customer.data;
  if (!c) return <div className="skel" style={{ height: 240 }} />;
  const anon = !!c.anonymizedAt;
  const rows = history.data?.data ?? [];
  const hasHistory = c.stats.quotes + c.stats.orders > 0 || rows.length > 0;
  return (
    <div className="col" style={{ gap: 18 }}>
      {onBack ? (
        <button
          type="button"
          className="btn sm g"
          style={{ alignSelf: 'flex-start', padding: 0 }}
          onClick={onBack}
        >
          <Icon name="chevronsLeft" size={16} />
          Clientes
        </button>
      ) : null}
      <section className="card col" style={{ gap: 14 }}>
        <div className="row between wrap" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 12 }}>
            <span
              className="avatar co"
              style={{ width: 48, height: 48, flexBasis: 48, fontSize: 18 }}
            >
              {anon ? '#' : initials(c.name)}
            </span>
            <div>
              <div className="row wrap" style={{ gap: 8 }}>
                <h2>{c.name}</h2>
                {anon ? <span className="badge">Anonimizado</span> : null}
              </div>
              <p className="sm muted">
                {anon && c.anonymizedAt
                  ? `anonimizado em ${formatDate(c.anonymizedAt)}${c.anonymizedBy ? ` por ${c.anonymizedBy}` : ''}`
                  : `cliente desde ${formatDate(c.createdAt)}`}
              </p>
            </div>
          </div>
          {anon ? null : (
            <button type="button" className="btn sm" onClick={() => setDialog('edit')}>
              Editar
            </button>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(160px,100%),1fr))',
            gap: 12,
          }}
        >
          <div>
            <p className="xs muted">WhatsApp</p>
            <p className="tab">{c.whatsapp ?? '—'}</p>
          </div>
          <div>
            <p className="xs muted">E-mail</p>
            <p className="trunc">{c.email ?? '—'}</p>
          </div>
          <div>
            <p className="xs muted">Apelido no marketplace</p>
            <p>{c.marketplaceNickname ?? '—'}</p>
          </div>
          <div>
            <p className="xs muted">Canal preferido</p>
            <p>{c.preferredChannel?.name ?? '—'}</p>
          </div>
        </div>
        <p className="sm" style={{ whiteSpace: 'pre-line' }}>
          <span className="muted">Notas:</span> {c.notes ?? '—'}
        </p>
        {anon ? null : (
          <details>
            <summary className="sm" style={{ cursor: 'pointer', fontWeight: 500 }}>
              Dados fiscais e endereço
            </summary>
            <div className="col" style={{ gap: 6, marginTop: 10 }}>
              <p className="xs muted">
                Só preencha quando precisar emitir NF ou enviar direto. CPF/CNPJ e endereço não
                aparecem em listas.
              </p>
              <p className="sm">
                <span className="muted">CPF/CNPJ:</span>{' '}
                <span className="tab">{c.taxId ?? '—'}</span>
              </p>
              <p className="sm">
                <span className="muted">Endereço:</span> {c.address ?? '—'}
              </p>
            </div>
          </details>
        )}
      </section>
      <section className="card flat">
        <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 10 }}>
          <h2>Histórico</h2>
          <div className="row" style={{ gap: 20 }}>
            <span className="sm">
              <span className="muted">Total gasto</span>{' '}
              <b className="tab" style={{ fontWeight: 600 }}>
                {formatBRL(history.data?.spentCentavos ?? c.stats.spentCentavos)}
              </b>
            </span>
            <span className="sm">
              <span className="muted">Pedidos</span>{' '}
              <b style={{ fontWeight: 600 }}>{history.data?.orders ?? c.stats.orders}</b>
            </span>
          </div>
        </div>
        <div className="gt">
          {rows.map((e) => (
            <Link
              key={`${e.kind}-${e.id}`}
              className="gtr"
              to={e.kind === 'quote' ? `/orcamentos/${e.id}` : `/pedidos/${e.id}`}
              style={{ gridTemplateColumns: '130px 1fr 110px 100px' }}
            >
              <span className="tab sm" style={{ color: 'var(--link)', fontWeight: 500 }}>
                {e.number}
              </span>
              <span className="trunc">{e.description || '—'}</span>
              <span>
                <HistoryBadge kind={e.kind} status={e.status} />
              </span>
              <span className="num">{formatBRL(e.valueCentavos)}</span>
            </Link>
          ))}
          {history.data && rows.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              <p className="sm">Ainda sem orçamentos ou pedidos.</p>
              <Link className="btn sm" to="/orcamento-rapido">
                Fazer orçamento
              </Link>
            </div>
          ) : null}
        </div>
      </section>
      {isAdmin && !anon ? (
        <div className="row between wrap" style={{ gap: 10 }}>
          <p className="xs muted" style={{ maxWidth: 420 }}>
            {hasHistory
              ? 'LGPD: anonimizar apaga nome, contatos e endereço, mas mantém os valores nos relatórios.'
              : 'Sem orçamentos nem pedidos: excluir manda para a lixeira por 30 dias.'}
          </p>
          {hasHistory ? (
            <button type="button" className="btn d sm" onClick={() => setDialog('anonymize')}>
              Anonimizar cliente
            </button>
          ) : (
            <button type="button" className="btn d sm" onClick={() => setDialog('delete')}>
              Excluir cliente
            </button>
          )}
        </div>
      ) : null}
      {dialog === 'edit' ? (
        <CustomerDialog
          customer={c}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            toast('Cliente atualizado');
          }}
        />
      ) : null}
      {dialog === 'anonymize' ? (
        <Modal
          title={`Anonimizar ${c.name}?`}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDialog(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn d"
                onClick={() => anonymize.mutate()}
                disabled={anonymize.isPending}
              >
                Anonimizar
              </button>
            </>
          }
        >
          <Alert tone="err">Não dá para desfazer. Atende a pedidos de exclusão pela LGPD.</Alert>
          <p className="sm">
            <b>Some:</b> nome, WhatsApp, e-mail, apelido, CPF/CNPJ, endereço e notas, as observações
            e o rastreio dos pedidos, as etiquetas de envio e as referências dos orçamentos.
          </p>
          <p className="sm">
            <b>Fica:</b> valores de orçamentos e pedidos ({formatBRL(c.stats.spentCentavos)}) e as
            notas fiscais, para relatórios e MEI continuarem certos.
          </p>
          <p className="sm">
            <b>Vira:</b> “Cliente anonimizado #N”.
          </p>
        </Modal>
      ) : null}
      {dialog === 'delete' ? (
        <Modal
          title={`Excluir ${c.name}?`}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDialog(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn d"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                Excluir
              </button>
            </>
          }
        >
          <p className="sm">Vai para a lixeira e pode ser restaurado por 30 dias.</p>
        </Modal>
      ) : null}
    </div>
  );
}

/** "Direto · WhatsApp", "ML · apelido", "dados pessoais removidos". */
function tagLine(c: Customer): string {
  if (c.anonymizedAt) return 'dados pessoais removidos';
  return (
    [c.preferredChannel?.name, c.marketplaceNickname ?? (c.whatsapp ? 'WhatsApp' : null)]
      .filter(Boolean)
      .join(' · ') || '—'
  );
}

/** Clientes: list with search + detail with the quote and order history (board Clientes). */
export function CustomersPage() {
  const phone = useIsPhone();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  const list = useCustomers(q);
  const rows = list.data?.data ?? [];
  const selected = params.get('id') ?? (phone ? null : (rows[0]?.id ?? null));
  const pick = (id: string) => setParams({ id });

  const newButton = (
    <div className="row wrap" style={{ gap: 10 }}>
      <ExportCsvButton dataset="clientes" params={q.trim() ? { q: q.trim() } : undefined} />
      <button type="button" className="btn p" onClick={() => setCreating(true)}>
        <Icon name="plus" size={18} />
        Cliente
      </button>
    </div>
  );
  const dialog = creating ? (
    <CustomerDialog
      onClose={() => setCreating(false)}
      onSaved={(c) => {
        setCreating(false);
        toast(`${c.name} cadastrada`);
        pick(c.id);
      }}
    />
  ) : null;

  if (list.error && !list.data) {
    return (
      <>
        <PageHead crumb="Vender" title="Clientes" />
        <div className="card">
          <ErrorBox
            title="Não foi possível carregar os clientes"
            onRetry={() => void list.refetch()}
          />
        </div>
      </>
    );
  }
  if (list.data && rows.length === 0 && !q.trim()) {
    return (
      <>
        <PageHead crumb="Vender" title="Clientes" actions={newButton} />
        <div className="card">
          <div className="empty">
            <div className="emptyart" />
            <h2>Nenhum cliente ainda</h2>
            <p>
              Clientes aparecem aqui quando você salva um orçamento ou registra um pedido com nome
              ou apelido.
            </p>
            <button type="button" className="btn p" onClick={() => setCreating(true)}>
              Cadastrar cliente
            </button>
          </div>
        </div>
        {dialog}
      </>
    );
  }

  const listCard = (
    <section
      className="card flat"
      style={phone ? undefined : { flex: '2 1 340px', minWidth: 0 }}
      aria-label="Lista de clientes"
    >
      <div style={{ padding: 12 }}>
        <label className="input">
          <Icon name="search" size={18} />
          <input
            className="qin"
            aria-label="Buscar cliente"
            placeholder="Nome, telefone ou apelido"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>
      <div className="gt">
        <div className="gtr head" style={{ gridTemplateColumns: '1fr 96px 64px' }}>
          <span>Cliente</span>
          <span className="num">Gasto</span>
          <span className="num">Última</span>
        </div>
        {!list.data
          ? [0, 1, 2].map((i) => (
              <div key={i} className="skel" style={{ height: 52, margin: '8px 16px' }} />
            ))
          : rows.map((c) => (
              <button
                key={c.id}
                type="button"
                className={c.id === selected ? 'gtr sel' : 'gtr'}
                style={{ gridTemplateColumns: '1fr 96px 64px' }}
                aria-pressed={c.id === selected}
                onClick={() => pick(c.id)}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 500, display: 'block' }} className="trunc">
                    {c.name}
                  </span>
                  <span className="xs muted">{tagLine(c)}</span>
                </span>
                <span className="num">{formatBRL(c.stats.spentCentavos)}</span>
                <span className="num sm">
                  {c.stats.lastPurchaseOn
                    ? formatIsoDate(c.stats.lastPurchaseOn).slice(0, 5)
                    : c.stats.lastQuoteAt
                      ? formatDate(c.stats.lastQuoteAt).slice(0, 5)
                      : '—'}
                </span>
              </button>
            ))}
        {list.data && rows.length === 0 ? (
          <div className="empty" style={{ padding: '28px 16px' }}>
            <h3>Nenhum cliente encontrado</h3>
            <p className="sm">Nada com “{q}” no nome, telefone ou apelido.</p>
            <button type="button" className="btn sm" onClick={() => setQ('')}>
              Limpar filtros
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );

  if (phone) {
    return (
      <div className="col" style={{ gap: 12 }}>
        {selected ? (
          <CustomerDetailView id={selected} onBack={() => setParams({})} />
        ) : (
          <>
            <PageHead title="Clientes" actions={newButton} />
            {listCard}
          </>
        )}
        {dialog}
      </div>
    );
  }
  return (
    <>
      <PageHead crumb="Vender" title="Clientes" actions={newButton} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        {listCard}
        <div style={{ flex: '3 1 460px', minWidth: 0 }}>
          {selected ? (
            <CustomerDetailView key={selected} id={selected} />
          ) : (
            <div className="skel" style={{ height: 240 }} />
          )}
        </div>
      </div>
      {dialog}
    </>
  );
}
